import { CircularProgress } from '@civilgrid/ui/components';
import { useFeatureFlagEnabled } from '@posthog/react';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import type { JSX } from 'react';
import { useEffect, useMemo } from 'react';
import { useSnapshot } from 'valtio';
import BackButton from '../../common/BackButton';
import CivilSearchLayout from '../../common/CivilSearchLayout';
import NavBar from '../../common/NavBar';
import NavButton from '../../common/NavButton';
import store from './store';
import Contacts from './tabs/contacts';
import Digitization from './tabs/digitization';
// sub tabs
import General from './tabs/general';
import History from './tabs/history';
import GeometryMap from './tabs/map';
import NewContact from './tabs/new-contact';
import SubGeometries from './tabs/subGeometries';

const tabConfig: { [key: string]: () => JSX.Element | null } = {
  general: General,
  contacts: Contacts,
  digitization: Digitization,
  history: History,
  'service-areas': GeometryMap,
  'sub-regions': SubGeometries,
  'new-contact': NewContact,
} as const;

export default function AgencyDetails() {
  const { agencyId, tab } = useParams({ strict: false }) as { agencyId: string; tab: string };
  // Untyped on purpose: routeTree.tsx gives these routes no `validateSearch`.
  const { showMerged } = useSearch({ strict: false }) as { showMerged?: unknown };
  const navigate = useNavigate();
  const { agency, isLoading, isError } = useSnapshot(store);
  const subRegionsEnabled = useFeatureFlagEnabled('agency-show-sub-regions');

  useEffect(() => {
    store.fetchData(agencyId);
    return () => store.reset();
  }, [agencyId]);

  // A child of a merged record opens its parent unless `?showMerged` asks for the child itself.
  const parentToOpen =
    agency?.id === agencyId && agency.parentAgencyId && showMerged === undefined ? agency.parentAgencyId : null;
  // Tab links keep `?showMerged` so switching tabs on a child does not trigger that redirect.
  const tabSearch = showMerged === undefined ? undefined : { showMerged };

  useEffect(() => {
    if (!parentToOpen) return;
    navigate({
      to: '/admin/civil-search/agencies/$agencyId/$tab',
      params: { agencyId: parentToOpen, tab: tab ?? 'general' },
      replace: true,
    });
  }, [parentToOpen, tab, navigate]);

  const content = useMemo(() => {
    // The Sub Regions tab is flag-gated. While the flag is still resolving (`undefined`), show a
    // loader for this tab rather than flashing the General tab and then swapping to Sub Regions
    // once it resolves. Once it resolves to off, ignore the route (e.g. a direct URL) and fall back.
    if (tab === 'sub-regions') {
      if (subRegionsEnabled === undefined) return <CircularProgress indeterminate />;
      if (!subRegionsEnabled) return <General />;
    }

    const Comp = tabConfig[tab] ?? tabConfig.general;
    return <Comp />;
  }, [tab, subRegionsEnabled]);

  const warning = agency?.status === 'archived';
  const huskInfo = agency?.mergedInto ?? null;

  // `fetchData` runs in an effect (after first render), so on a fresh page load the store
  // is still at its initial state (isLoading=false, agency=undefined). Guard that window
  // too, otherwise tabs render with `agency` undefined and crash on `agency!.id`.
  // Also covers the redirect: the child stays in the store until the parent's fetch starts.
  if (isLoading || (!agency && !isError) || parentToOpen || (agency?.parentAgencyId && agency.id !== agencyId)) {
    return (
      <div>
        <CircularProgress indeterminate />
      </div>
    );
  }

  if (isError) {
    return <div>Error fetching the agency.</div>;
  }

  return (
    <CivilSearchLayout
      warning={warning}
      title={
        <div className="flex items-center space-x-2">
          <BackButton to="/agencies">Back</BackButton>
          <span>{agency?.id ? `${agency.agencyName} - ${agency.type}` : 'Agency Details'}</span>

          {!!warning && (
            <span className="max-w-[50%] rounded border border-red-500 p-2 text-xs">
              This agency is <code className="font-bold">archived</code> and unavailable for data requests and viewing
              in the Utilities Tab. Contact Jack, Sharath, or Braden to re-activate this agency.
            </span>
          )}

          {huskInfo && (
            <span
              data-testid="agency-husk-banner"
              className="max-w-[60%] rounded border border-amber-500 bg-amber-50 p-2 text-xs"
            >
              This agency was <strong>merged into</strong>{' '}
              <Link
                to="/admin/civil-search/agencies/$agencyId/$tab"
                params={{ agencyId: huskInfo.targetId, tab: 'general' }}
                className="font-semibold underline"
                data-testid="agency-husk-banner-target-link"
              >
                {huskInfo.targetAgencyName}
              </Link>{' '}
              on {new Date(huskInfo.mergedAt).toLocaleDateString()} by {huskInfo.mergedByName}. Edits here have no
              effect.
            </span>
          )}
        </div>
      }
    >
      <div className="flex h-[calc(100vh_-_var(--spacing-top-nav-height)_-_4rem)] flex-col space-y-2 overflow-y-clip">
        <NavBar>
          <NavButton
            to={`/admin/civil-search/agencies/${agencyId}/general`}
            search={tabSearch}
            isActive={tab === 'general' || !tab}
          >
            General
          </NavButton>
          <NavButton
            to={`/admin/civil-search/agencies/${agencyId}/contacts`}
            search={tabSearch}
            isActive={tab === 'contacts' || tab === 'new-contact'}
          >
            Contacts
          </NavButton>
          <NavButton
            to={`/admin/civil-search/agencies/${agencyId}/digitization`}
            search={tabSearch}
            isActive={tab === 'digitization'}
          >
            Digitization
          </NavButton>
          <NavButton
            to={`/admin/civil-search/agencies/${agencyId}/history`}
            search={tabSearch}
            isActive={tab === 'history'}
            dataTestId="agency-history-tab"
          >
            History
          </NavButton>
          <NavButton
            to={`/admin/civil-search/agencies/${agencyId}/service-areas`}
            search={tabSearch}
            isActive={tab === 'service-areas'}
            dataTestId="agency-tab-service-areas"
          >
            Service Areas
          </NavButton>
          {subRegionsEnabled && (
            <NavButton
              to={`/admin/civil-search/agencies/${agencyId}/sub-regions`}
              search={tabSearch}
              isActive={tab === 'sub-regions'}
              dataTestId="agency-tab-sub-regions"
            >
              Sub Regions
            </NavButton>
          )}
        </NavBar>
        <div className="grow overflow-y-auto">{content}</div>
      </div>
    </CivilSearchLayout>
  );
}
